import { useMemo } from 'react';
import { useAuth } from '@aimsos/auth';
import { usePersona } from '../permissions';
import { resolveRequesterId } from '../../utils';
import { DEMO_END_USER } from '../../../../services';

/**
 * The effective request owner for the current persona (CT-4203).
 *
 * - **admin** persona → the real signed-in user (resolveRequesterId + name), so
 *   requests they file are stamped with their own identity.
 * - **user** persona (demo) → a fixed seed end-user (DEMO_END_USER = Danilo Petrov),
 *   so "My requests" shows that user's seed requests and anything filed while in
 *   user view is attributed to them — a coherent mock of the end-user experience.
 *
 * MOCK-only: with real auth + RBAC (CT-3904 / CT-3900) the identity always comes
 * from the session and the persona branch is removed.
 *
 * @returns {{ requesterId: string, requesterName: string }}
 */
export function useEffectiveRequester() {
  const { persona } = usePersona();
  const { user } = useAuth() ?? {};

  return useMemo(() => {
    if (persona === 'user') {
      return { requesterId: DEMO_END_USER.id, requesterName: DEMO_END_USER.name };
    }
    return {
      requesterId: resolveRequesterId(user),
      requesterName: user?.name ?? user?.email ?? '',
    };
  }, [persona, user]);
}
